import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import ConfirmDialog from "@/components/confirm-dialog";
import { PageHeader } from "@/components/page-header";
import SiteSetup from "@/components/site-setup";
import { FormErrorAlert } from "@/components/stone/form-errors";
import { Badge } from "@/components/ui/badge";
import { getErrorMessage } from "@/lib/form-errors";
import { formatSiteStatus, getSiteStatusBadgeVariant } from "@/lib/site-status";
import type { Site } from "@/types";
import { trpc } from "@/utils/trpc";

export default function SiteEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const cancelTarget = id ? `/sites/${id}` : "/sites";
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const site = useQuery({
    ...trpc.getSite.queryOptions({ id: id || "" }),
    enabled: Boolean(id),
  });

  const deleteMutation = useMutation({
    ...trpc.deleteSite.mutationOptions(),
    onSuccess: () => {
      toast.success("Site deleted");
      navigate("/sites");
    },
    onError: (error) => {
      const message = getErrorMessage(error, "Failed to delete site");
      setDeleteError(message);
      toast.error(message);
    },
  });

  if (site.isLoading) {
    return null;
  }

  if (site.isError || !site.data) {
    return (
      <FormErrorAlert
        message={
          site.isError
            ? getErrorMessage(site.error, "Failed to load site")
            : "Site not found"
        }
        title={site.isError ? "Something went wrong" : "Not found"}
      />
    );
  }

  return (
    <>
      <PageHeader
        title="Edit site"
        actions={[
          {
            label: "Delete Site",
            onClick: () => setShowDeleteDialog(true),
            variant: "destructive",
            disabled: deleteMutation.isPending,
          },
        ]}
        tags={
          <Badge
            variant={getSiteStatusBadgeVariant(site.data.status)}
            className="uppercase"
            aria-label={`Site status: ${formatSiteStatus(site.data.status)}`}
          >
            {formatSiteStatus(site.data.status)}
          </Badge>
        }
      />

      <SiteSetup
        site={site.data as Site}
        onCancel={() => navigate(cancelTarget)}
      />

      <ConfirmDialog
        open={showDeleteDialog}
        onOpenChange={(open) => {
          setShowDeleteDialog(open);
          if (!open) setDeleteError(null);
        }}
        title="Delete this site?"
        description={
          <div>
            <strong>This will permanently remove the site and its data.</strong>
            <ul className="mt-2 list-inside list-disc">
              <li>All sign-in records for this site will be deleted</li>
              <li>
                All manager and worker approvals for this site will be removed
              </li>
              <li>This action cannot be undone</li>
            </ul>
          </div>
        }
        confirmLabel="Yes, delete site"
        pendingLabel="Deleting..."
        pending={deleteMutation.isPending}
        error={deleteError}
        onConfirm={async () => {
          if (!id) return;
          await deleteMutation.mutateAsync({ id });
        }}
      />
    </>
  );
}
